import { type CSSProperties, type ReactNode, type Ref } from "react"
import { Skeleton, useMediaQuery, useTheme } from "@mui/material"
import { colors } from "../../services/config/colors"

type TypographyVariant = "h1" | "h2" | "h3" | "h4" | "p"

interface TypographyProps {
  children?: ReactNode
  id?: string
  ref?: Ref<HTMLDivElement>
  fontSize?: number // in px, will be converted to rem
  fontWeight?: number | string
  color?: string
  uppercase?: boolean
  ellipsis?: boolean
  maxLines?: number
  className?: string
  style?: CSSProperties
  variant?: TypographyVariant
  skeleton?: boolean
  skeletonMaxWidth?: number | string
}

// Default font sizes for each variant (in pixels)
const mobileDefaultFontSizes: Record<TypographyVariant, number> = {
  h1: 22,
  h2: 20,
  h3: 18,
  h4: 14,
  p: 14,
}

const desktopDefaultFontSizes: Record<TypographyVariant, number> = {
  h1: 28,
  h2: 26,
  h3: 20,
  h4: 18,
  p: 14,
}

// Default font weights for each variant
const defaultFontWeights: Record<TypographyVariant, number> = {
  h1: 800,
  h2: 800,
  h3: 700,
  h4: 700,
  p: 400,
}

const Typography = ({
  children,
  id,
  ref,
  fontSize,
  fontWeight,
  color = colors.text,
  uppercase,
  ellipsis,
  maxLines = 1,
  className = "",
  style = {},
  variant = "p",
  skeleton,
  skeletonMaxWidth = 160,
}: TypographyProps) => {
  const theme = useTheme()
  const matchesMD = useMediaQuery(theme.breakpoints.down("md"))

  // Use provided fontSize or default for the variant
  const finalFontSize =
    fontSize ??
    (matchesMD ? mobileDefaultFontSizes : desktopDefaultFontSizes)[variant]

  // Use provided fontWeight or default for the variant
  const finalFontWeight = fontWeight ?? defaultFontWeights[variant]

  // Convert px to rem (assuming 16px = 1rem)
  const fontSizeRem = `${finalFontSize / 16}rem`

  const combinedStyle: CSSProperties = {
    fontSize: fontSizeRem,
    color,
    fontWeight: finalFontWeight,
    ...(uppercase ? { textTransform: "uppercase" } : {}),
    ...(ellipsis
      ? {
          display: "-webkit-box",
          WebkitLineClamp: maxLines,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          wordBreak: "break-word",
        }
      : {}),
    ...style,
  }

  const Component = variant

  if (skeleton) {
    return (
      <Skeleton
        className={className}
        style={{
          maxWidth: skeletonMaxWidth,
          ...combinedStyle,
        }}
      />
    )
  }

  return (
    <Component id={id} ref={ref} className={className} style={combinedStyle}>
      {children}
    </Component>
  )
}

export default Typography
